import apiClient from '../../../lib/client';

// Abort controllers for each state scraper run (for cancellation)
const scraperAbortControllers = new Map();

function normalizeStateCode(code) {
  return String(code || '')
    .trim()
    .toUpperCase();
}

export const scrapersAPI = {
  // State scrapers
  listStateScrapers: () => apiClient.get('/scrapers/states').then((r) => r.data),

  runStateScraper: (code) => {
    const normalizedCode = normalizeStateCode(code);
    const controller = new AbortController();
    scraperAbortControllers.set(normalizedCode, controller);

    return apiClient
      .post(
        `/scrapers/states/${normalizedCode}`,
        {},
        { signal: controller.signal, timeout: 1_800_000 }
      )
      .then((r) => {
        scraperAbortControllers.delete(normalizedCode);
        return r.data;
      })
      .catch((err) => {
        scraperAbortControllers.delete(normalizedCode);
        throw err;
      });
  },

  stopStateScraper: (code) => {
    const normalizedCode = normalizeStateCode(code);
    const controller = scraperAbortControllers.get(normalizedCode);
    if (controller) {
      controller.abort();
      scraperAbortControllers.delete(normalizedCode);
    }
  },

  resetStaleScraperStatus: (code) =>
    apiClient.post(`/scrapers/states/${normalizeStateCode(code)}/reset-status`).then((r) => r.data),

  // Stream scraper run with real-time SSE-like response stream
  // Caller must handle the streamed response body manually
  runStateScraperStream: (code, { cacheMode = 'write', verbose = false } = {}) => {
    const normalizedCode = normalizeStateCode(code);

    const params = new URLSearchParams();
    if (cacheMode && cacheMode !== 'off') {
      params.append('cacheMode', cacheMode);
    }
    if (verbose) {
      params.append('verbose', 'true');
    }

    const queryString = params.toString();
    const url = `/api/scrapers/states/${normalizedCode}/run-stream${queryString ? `?${queryString}` : ''}`;

    return fetch(url, { method: 'POST' }).then((res) => {
      if (!res.ok) {
        throw new Error(`Failed to start scraper stream: ${res.status} ${res.statusText}`);
      }
      return res;
    });
  },

  getCachedWebpageUrl: (code) =>
    `/api/scrapers/states/${encodeURIComponent(normalizeStateCode(code))}/cached-webpage`,

  getArtifactReview: (code) =>
    apiClient
      .get(`/scrapers/states/${normalizeStateCode(code)}/artifact-review`)
      .then((r) => r.data),

  markArtifactReview: (code, data) =>
    apiClient
      .patch(`/scrapers/states/${normalizeStateCode(code)}/artifact-review`, data)
      .then((r) => r.data),

  runAllStateScrapers: (opts = {}) =>
    apiClient.post('/scrapers/states', opts, { timeout: 600_000 }).then((r) => r.data),

  // County scrapers
  runCountyScraper: (authorityId) =>
    apiClient.post(`/scrapers/county/${authorityId}`).then((r) => r.data),

  runCountyBatch: (state, opts = {}) =>
    apiClient
      .post(`/scrapers/county-batch/${normalizeStateCode(state)}`, opts, { timeout: 600_000 })
      .then((r) => r.data),

  // Website checks
  runWebsiteCheck: (authorityId) =>
    apiClient.post(`/scrapers/website-check/${authorityId}`).then((r) => r.data),

  runWebsiteCheckBatch: (opts = {}) =>
    apiClient.post('/scrapers/website-check-batch', opts, { timeout: 600_000 }).then((r) => r.data),

  // Logs
  getLogs: (params = {}) => apiClient.get('/scrapers/logs', { params }).then((r) => r.data),

  // Scheduler
  getScheduler: () => apiClient.get('/scrapers/scheduler').then((r) => r.data),

  // Discovery
  discoverState: (code) =>
    apiClient
      .post(`/scrapers/discovery/${normalizeStateCode(code)}`, {}, { timeout: 900_000 })
      .then((r) => r.data),

  getDiscoveryStats: (code) =>
    apiClient.get(`/scrapers/discovery/${normalizeStateCode(code)}`).then((r) => r.data),

  listDiscoverySessions: (limit = 20) =>
    apiClient.get('/scrapers/discovery/sessions', { params: { limit } }).then((r) => r.data),

  getDiscoverySessions: (code) =>
    apiClient.get(`/scrapers/discovery/sessions/${normalizeStateCode(code)}`).then((r) => r.data),

  // Website Finder — stats only (SSE stream handled directly in the store)
  getWebsiteFinderStats: (params = {}) =>
    apiClient.get('/authorities', { params: { ...params, limit: 1 } }).then((r) => r.data),

  // Elections by scraper data source
  getElectionsByDataSource: (dataSourceKey) =>
    apiClient.get('/elections', { params: { dataSource: dataSourceKey } }).then((r) => r.data),

  // State authority URLs
  getStateAuthorityUrls: (code) =>
    apiClient
      .get(`/scrapers/states/${normalizeStateCode(code)}/authority-urls`)
      .then((r) => r.data),

  updateStateAuthorityUrls: (code, data) =>
    apiClient
      .patch(`/scrapers/states/${normalizeStateCode(code)}/authority-urls`, data)
      .then((r) => r.data),
};
